import React from 'react'
import './ExplainabilityCard.css'

/**
 * ExplainabilityCard - Displays AI decision transparency information
 *
 * Shows:
 * - Which agent handled the request
 * - Confidence level (with color coding)
 * - Reasoning explanation
 * - Model used (if available)
 */
function ExplainabilityCard({ explainability, collapsed = false }) {
  const [isCollapsed, setIsCollapsed] = React.useState(collapsed)

  if (!explainability) {
    return null
  }

  const {
    agent_display_name,
    agent_selected,
    confidence,
    reasoning,
    model_used,
    threshold_met,
    fallback_used
  } = explainability

  const getConfidenceInfo = (conf) => {
    if (conf === null || conf === undefined) {
      return { tier: 'na', label: 'N/A', icon: '❓' }
    }
    const confPercent = Math.round(conf * 100)
    if (conf >= 0.7) {
      return { tier: 'high', label: `${confPercent}%`, icon: '🟢' }
    }
    if (conf >= 0.5) {
      return { tier: 'medium', label: `${confPercent}%`, icon: '🟡' }
    }
    return { tier: 'low', label: `${confPercent}%`, icon: '🔴' }
  }

  const confidenceInfo = getConfidenceInfo(confidence)

  const getAgentIcon = (agent) => {
    switch (agent) {
      case 'grocery':
        return '🛒'
      case 'recipe':
        return '👨‍🍳'
      case 'document':
        return '📄'
      case 'monolithic':
        return '🤖'
      case 'preference':
        return '⚙️'
      default:
        return '🤖'
    }
  }

  return (
    <div className="explainability-card">
      <div
        className="explainability-card-toggle"
        onClick={() => setIsCollapsed(!isCollapsed)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setIsCollapsed(!isCollapsed)
          }
        }}
        aria-expanded={!isCollapsed}
        aria-label="Toggle explainability details"
      >
        <div className="explainability-card-summary">
          <span className="explainability-card-agent-icon">{getAgentIcon(agent_selected)}</span>
          <span className="explainability-card-agent-name">{agent_display_name}</span>
          <span className="explainability-card-confidence" data-tier={confidenceInfo.tier}>
            <span>{confidenceInfo.icon}</span>
            <span>{confidenceInfo.label}</span>
          </span>
          {fallback_used && (
            <span className="explainability-card-fallback">(fallback)</span>
          )}
        </div>
        <span
          className={`explainability-card-chevron ${isCollapsed ? 'explainability-card-chevron--collapsed' : 'explainability-card-chevron--expanded'}`}
        >
          ▼
        </span>
      </div>

      {!isCollapsed && (
        <div className="explainability-card-body">
          {reasoning && (
            <div className="explainability-card-reasoning">
              <strong>Reasoning:</strong> {reasoning}
            </div>
          )}

          <div className="explainability-card-meta">
            {model_used && (
              <div>
                <strong>Model:</strong> {model_used}
              </div>
            )}
            {threshold_met !== undefined && (
              <div>
                <strong>Threshold:</strong> {threshold_met ? '✅ Met' : '❌ Not met'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default ExplainabilityCard
